import { useMemo } from "react";
import { useLocalization } from "@fluent/react";
import { Accordion, Alert, formatNumber, LoadingShim } from "@narmi/design_system";
import { format, subMonths } from "date-fns";
import type Account from "byzantine/src/Account";
import type { AccountMonthlyTotals } from "./TrendChart/hooks";
import styles from "./NetWorthGainsLosses.module.scss";

type NetWorthGainsLossesProps = {
  /** The month this breakdown covers — any date within that month works. */
  month: Date;
  /** Already filtered to whatever's currently selected (and non-hidden). */
  accounts: Account[];
  accountTrends: AccountMonthlyTotals[];
  isLoading: boolean;
  /** True if any one account's request failed - its delta zeroes out rather
   * than being fabricated, so this may under-represent gains/losses. */
  hasError: boolean;
};

type AccountDelta = {
  account: Account;
  delta: number;
};

const AccordionHeader = ({
  isOpen,
  label,
  amount,
  className
}: {
  isOpen: boolean;
  label: string;
  amount: number;
  className: string;
}) => (
  <div className={className}>
    <span>{label}</span>
    <span className={styles.headerEnd}>
      {formatNumber(amount, "currency")}
      <span className={`narmi-icon-chevron-${isOpen ? "up" : "down"}`} />
    </span>
  </div>
);

const AccountDeltaRows = ({ deltas }: { deltas: AccountDelta[] }) => (
  <div className={styles.accountList}>
    {deltas.map(({ account, delta }) => (
      <div key={account.id} className={styles.accountRow}>
        <span>{account.getName()}</span>
        <span>{formatNumber(delta, "currency")}</span>
      </div>
    ))}
  </div>
);

/**
 * One top-level Gains or Losses group: expandable (with a chevron) when
 * there are accounts to show, otherwise a plain, non-interactive $0 row.
 */
const TotalGroup = ({
  testId,
  label,
  deltas
}: {
  testId: string;
  label: string;
  deltas: AccountDelta[];
}) => {
  const total = deltas.reduce((sum, { delta }) => sum + delta, 0);

  if (deltas.length === 0) {
    return (
      <div className={styles.topLevelGroup} data-testid={testId}>
        <div className={styles.topLevelHeader}>
          <span>{label}</span>
          <span>{formatNumber(0, "currency")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.topLevelGroup}>
      <Accordion
        testId={testId}
        renderSummary={(isOpen) => (
          <AccordionHeader
            isOpen={isOpen}
            label={label}
            amount={total}
            className={styles.topLevelHeader}
          />
        )}
      >
        <AccountDeltaRows deltas={deltas} />
      </Accordion>
    </div>
  );
};

/**
 * Pure "[Month] - Gains & losses" content: two top-level groups (Gains,
 * Losses) each listing the accounts whose balance moved that direction that
 * month, with the amount they moved by. A group with nothing in it shows as
 * a plain $0 row rather than an empty expandable accordion, and if neither
 * group has anything, the whole thing is replaced by an empty-state message.
 * Renders only this content, with no drawer wrapper or trigger button of
 * its own — the caller decides how this gets shown (e.g. inside BorderCard's
 * in-card drawer).
 */
const NetWorthGainsLosses = ({
  month,
  accounts,
  accountTrends,
  isLoading,
  hasError
}: NetWorthGainsLossesProps) => {
  const { l10n } = useLocalization();

  const deltas = useMemo<AccountDelta[]>(() => {
    const monthKey = format(month, "yyyy-MM");
    const previousMonthKey = format(subMonths(month, 1), "yyyy-MM");
    const trendsByAccountId = new Map(
      accountTrends.map((trend) => [trend.accountId, trend])
    );

    return accounts.map((account) => {
      const totalsByMonth =
        trendsByAccountId.get(account.id)?.totalsByMonth ?? {};
      const delta =
        (totalsByMonth[monthKey] ?? 0) - (totalsByMonth[previousMonthKey] ?? 0);
      return { account, delta };
    });
  }, [accounts, accountTrends, month]);

  const gains = deltas.filter(({ delta }) => delta > 0);
  const losses = deltas.filter(({ delta }) => delta < 0);
  const hasAnyChange = gains.length > 0 || losses.length > 0;

  return (
    <div
      className={styles.container}
      data-testid="net-worth-gains-losses-content"
    >
      <h4 className={styles.drawerTitle}>
        {l10n.getString("net-worth-gains-losses-title", {
          month: format(month, "MMM yyyy"),
        })}
      </h4>
      <div className={styles.scrollArea}>
        {/* LoadingShim's wrapper has no set height, so it must sit inside .scrollArea, not wrap it. */}
        <LoadingShim isLoading={isLoading}>
          <Alert
            isActive={hasError}
            isDismissable={false}
            kind="warn"
            paddingSize="xxs"
          >
            {l10n.getString("net-worth-gains-losses-error")}
          </Alert>
          {hasAnyChange ? (
            <>
              <TotalGroup
                testId="net-worth-gains-losses-gains"
                label={l10n.getString("label-net-worth-gains")}
                deltas={gains}
              />
              <TotalGroup
                testId="net-worth-gains-losses-losses"
                label={l10n.getString("label-net-worth-losses")}
                deltas={losses}
              />
            </>
          ) : (
            <p
              className={styles.emptyState}
              data-testid="net-worth-gains-losses-empty"
            >
              {l10n.getString("net-worth-gains-losses-empty")}
            </p>
          )}
        </LoadingShim>
      </div>
    </div>
  );
};

export default NetWorthGainsLosses;
